import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BookDemoModal from '../BookDemoModal/BookDemoModal';
import {
  FiMessageCircle, FiPhone, FiVideo, FiArrowRight, FiCheck, FiLayers, FiBarChart2, FiUsers, FiCpu, FiRefreshCw, FiSettings
} from 'react-icons/fi';
import logo from '../../Images/logo.png';
import HarryCasual from '../../assets/a2.avif';
import MegCasual from '../../assets/a1.avif';
import styles from './PlatformPage.module.css';

const PlatformPage = () => {
  const navigate = useNavigate();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.container}>
      {/* Ambient glows */}
      <div className={styles.ambientGlows}>
        <div className={styles.glow1}></div>
        <div className={styles.glow2}></div>
        <div className={styles.glow3}></div>
      </div>

      {/* 🟦 1. HEADER / NAVIGATION */}
      <header className={styles.header} style={{ flex: "none" }}>
        <div className={styles.headerInner}>
          <div className={styles.logoArea} onClick={() => navigate('/')}>
            <img src={logo} alt="SaleSense Logo" className={styles.logoImg} />
            <span className={styles.logoText}>Sailsense <span className={styles.logoCyan}>AI</span></span>
          </div>
          <nav className={styles.navLinks}>
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <a href="/ai-agents" onClick={(e) => { e.preventDefault(); navigate('/ai-agents'); }}>AI Agents</a>
            <a href="/platform" className={styles.activeLink} onClick={(e) => { e.preventDefault(); }}>Platform</a>
            <a href="/sales-operations" onClick={(e) => { e.preventDefault(); navigate('/sales-operations'); }}>Sales Operations</a>
            <a href="/solutions" onClick={(e) => { e.preventDefault(); navigate('/solutions'); }}>Solutions</a>
            <a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a>
            <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a>
          </nav>
          <div className={styles.headerActions}>
            <button className={styles.trialBtn} onClick={() => navigate('/login')}>
              login <FiArrowRight />
            </button>
          </div>
        </div>
      </header>

      {/* Grid line overlay */}
      <div className={styles.gridOverlay}></div>

      {/* 🟦 2. PLATFORM INTRO & FIGMA-STYLE WORKFLOW DIAGRAM */}
      <section className={styles.sectionBlock}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            Platform
          </div>
          <h1 className={styles.sectionCenterTitle}>Everything your sales <br />team needs. Automated.</h1>
          <p className={styles.sectionSubtitle}>
            A complete sales and customer engagement platform built to centralize operations, automate workflows, and accelerate conversions.
          </p>
        </div>

        {/* 🟦 Figma-inspired Interactive Neon Dashboard Mockup */}
        <div className={styles.dashboardVisualizationContainer}>
          <div className={styles.neonFrame}>
            <div className={styles.neonFrameInner}>

              {/* Dashboard Mock Shell */}
              <div className={styles.mockDashboard}>
                <div className={styles.mockHeader}>
                  <div className={styles.windowDots}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className={styles.searchBarMock}>Search leads...</div>
                  <div className={styles.headerRightMock}>
                    <span className={styles.timeMock}>09:41 AM</span>
                    <span className={styles.badgeActiveMock}>Live</span>
                  </div>
                </div>

                <div className={styles.mockContent}>
                  {/* Left Controls/Chat Graph mock */}
                  <div className={styles.mockLeftPanel}>
                    <div className={styles.mockDataCard}>
                      <span className={styles.cardLabelMock}>Conversations</span>
                      <div className={styles.chatMessageItem}>
                        <div className={styles.chatBubbleMock}>Interested in the new SUV variant.</div>
                      </div>
                      <div className={styles.chatMessageItemLeft}>
                        <div className={styles.chatBubbleMockLeft}>The SUV starts at ₹14.5L. EMG options available.</div>
                      </div>
                    </div>

                    {/* Audio wave generator mock */}
                    <div className={styles.waveContainerMock}>
                      <div className={styles.waveHeaderMock}>
                        <FiPhone className={styles.phoneIconMock} />
                        <span>ROVIA Voice Call Stream</span>
                      </div>
                      <div className={styles.soundwaves}>
                        <div className={styles.soundwaveBar} style={{ height: "40%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "70%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "50%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "90%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "60%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "80%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "45%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "30%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "75%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "55%" }}></div>
                        <div className={styles.soundwaveBar} style={{ height: "85%" }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Right Portraits mock */}
                  <div className={styles.mockRightPanel}>
                    <div className={styles.avatarVideoFrame}>
                      <img src={MegCasual} alt="AIMEE Agent" className={styles.avatarFrameImg} />
                      <span className={styles.avatarFrameLabel}>AIMEE - Video Presenter</span>
                    </div>
                    <div className={styles.avatarVideoFrameSmall}>
                      <img src={HarryCasual} alt="Sales Representative" className={styles.avatarFrameImg} />
                      <span className={styles.avatarFrameLabel}>Harry - Sales Rep</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating connector nodes/badges around the dashboard container */}
              <div className={`${styles.floatingBadge} ${styles.badgeTopLeft}`}>
                <FiMessageCircle />
              </div>
              <div className={`${styles.floatingBadge} ${styles.badgeTopRight}`}>
                <FiPhone />
              </div>
              <div className={`${styles.floatingBadge} ${styles.badgeBottomLeft}`}>
                <FiVideo />
              </div>
              <div className={`${styles.floatingBadge} ${styles.badgeBottomRight}`}>
                <span>Live Agent</span>
              </div>

              {/* Node connecting paths (represented dynamically via absolute positions) */}
              <div className={styles.connectorLineHorizontal}></div>
              <div className={styles.connectorLineVertical}></div>

            </div>
          </div>
        </div>
      </section>

      {/* 🟦 3. CORE MODULES SECTION */}
      <section className={styles.sectionBlock} style={{ borderTop: "1px solid rgba(255, 255, 255, 0.05)" }}>
        <div className={styles.sectionCenter}>
          <div className={styles.badgeCyan}>
            Core Modules
          </div>
          <h2 className={styles.sectionCenterTitle}>One platform. Every workflow.</h2>
        </div>

        <div className={styles.modulesGrid}>
          {/* Smart Dashboard */}
          <div className={styles.moduleCard}>
            <div className={styles.moduleCardHeader}>
              <div className={styles.moduleIconBox}>
                <FiBarChart2 />
              </div>
              <h4 className={styles.moduleTitle}>Smart Dashboard</h4>
            </div>
            <p className={styles.moduleDesc}>
              Track sales performance, lead flow, and conversion metrics in real time.
            </p>
            <ul className={styles.moduleList}>
              <li><FiCheck className={styles.cyanCheck} /> Admin Dashboard</li>
              <li><FiCheck className={styles.cyanCheck} /> Dealership Dashboard</li>
              <li><FiCheck className={styles.cyanCheck} /> Sales Insights</li>
              <li><FiCheck className={styles.cyanCheck} /> Pipeline Analytics</li>
            </ul>
          </div>

          {/* User Management */}
          <div className={styles.moduleCard}>
            <div className={styles.moduleCardHeader}>
              <div className={styles.moduleIconBox}>
                <FiUsers />
              </div>
              <h4 className={styles.moduleTitle}>User Management</h4>
            </div>
            <p className={styles.moduleDesc}>
              Control access and permissions across your organization.
            </p>
            <ul className={styles.moduleList}>
              <li><FiCheck className={styles.cyanCheck} /> Role Management</li>
              <li><FiCheck className={styles.cyanCheck} /> Team Permissions</li>
              <li><FiCheck className={styles.cyanCheck} /> Department Access</li>
              <li><FiCheck className={styles.cyanCheck} /> Workflow Assignment</li>
            </ul>
          </div>

          {/* Avatar Studio */}
          <div className={styles.moduleCard}>
            <div className={styles.moduleCardHeader}>
              <div className={styles.moduleIconBox}>
                <FiCpu />
              </div>
              <h4 className={styles.moduleTitle}>Avatar Studio</h4>
            </div>
            <p className={styles.moduleDesc}>
              Build and customize AI-powered digital representatives.
            </p>
            <ul className={styles.moduleList}>
              <li><FiCheck className={styles.cyanCheck} /> Avatar Selection</li>
              <li><FiCheck className={styles.cyanCheck} /> Voice Selection</li>
              <li><FiCheck className={styles.cyanCheck} /> AI Personality Settings</li>
              <li><FiCheck className={styles.cyanCheck} /> Interactive Agent Playground</li>
            </ul>
          </div>

          {/* Subscription Management */}
          <div className={styles.moduleCard}>
            <div className={styles.moduleCardHeader}>
              <div className={styles.moduleIconBox}>
                <FiRefreshCw />
              </div>
              <h4 className={styles.moduleTitle}>Subscription Management</h4>
            </div>
            <p className={styles.moduleDesc}>
              Scale automation as your business grows.
            </p>
            <ul className={styles.moduleList}>
              <li><FiCheck className={styles.cyanCheck} /> Flexible Plans</li>
              <li><FiCheck className={styles.cyanCheck} /> Agent Packages</li>
              <li><FiCheck className={styles.cyanCheck} /> Usage Tracking</li>
              <li><FiCheck className={styles.cyanCheck} /> Billing Management</li>
            </ul>
          </div>
        </div>

        {/* Deploy AI sales team CTA */}
        <div className={styles.deployCtaBox}>
          <h3 className={styles.deployCtaHeading}>See the platform in action.</h3>
          <div className={styles.deployCtaButtons}>
            <button className={styles.heroBtnCyan} onClick={() => setIsDemoModalOpen(true)}>
              Book a Demo <FiArrowRight />
            </button>
            <button className={styles.deployPricingBtn} onClick={() => navigate('/login')}>
              Sales Operations
            </button>
          </div>
        </div>
      </section>

      {/* 🟦 4. FOOTER */}
      <footer className={styles.footerSection}>
        <div className={styles.footerInner}>
          {/* Brand area left */}
          <div className={styles.footerBrandColumn}>
            <div className={styles.footerBrandLogo} onClick={() => navigate('/')}>
              <img src={logo} alt="SaleSense Logo" className={styles.footerLogoImg} />
              <span>Sailsense AI</span>
            </div>
            <p className={styles.footerBrandText}>
              AI Chat, Voice and Video agents that capture leads, automate conversations and close sales — 24/7.
            </p>
            <p className={styles.footerBrandProductOf}>
              A product of <strong>Provaantech</strong>
            </p>
          </div>

          {/* Brand System center */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Brand System</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>
                <FiMessageCircle style={{ marginRight: '0.5rem' }} /> Sailsense Chatbot
              </li>
              <li className={styles.footerLinkItem}>
                <FiPhone style={{ marginRight: '0.5rem' }} /> ROVIA — Voice AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiVideo style={{ marginRight: '0.5rem' }} /> AIMEE — Video AI
              </li>
              <li className={styles.footerLinkItem}>
                <FiSettings style={{ marginRight: '0.5rem' }} /> Sailsense Platform
              </li>
            </ul>
          </div>

          {/* Industries right */}
          <div className={styles.footerLinkColumn}>
            <h4 className={styles.footerColHeader}>Focus Industry</h4>
            <ul className={styles.footerLinksList}>
              <li className={styles.footerLinkItem}>Automotive Dealerships</li>
            </ul>
          </div>
        </div>

        {/* Bottom row copyright */}
        <div className={styles.footerBottomBar}>
          <div>© 2026 Sailsense AI - Provaantech. All rights reserved.</div>
          <div>Built for high-performance sales teams.</div>
        </div>
      </footer>
      <BookDemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
    </div>
  );
};

export default PlatformPage;
